import axios from 'axios';

const API_URL = '/api';
axios.defaults.withCredentials = true;

const createApiService = () => {
    const api = axios.create({
        baseURL: '/api',
        withCredentials: true
    });
    return api;
};

// get slug from URL for workspace-specific API calls
const getSlugFromURL = () => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    return pathParts.length >= 2 ? pathParts[1] : null;
};

const api = createApiService();
export const setupLoaderInterceptors = (startLoader, stopLoader) => {
    if (api.interceptors.request.handlers.length > 0) {
        api.interceptors.request.clear();
    }
    if (api.interceptors.response.handlers.length > 0) {
        api.interceptors.response.clear();
    }

    // Add request interceptor
    api.interceptors.request.use(config => {
        const slug = getSlugFromURL();
        if (slug) {
            config.headers['X-Client-Slug'] = slug;
        }
        const requestId = `${config.method}-${config.url}-${Date.now()}`;
        config.requestId = requestId;
        startLoader(requestId); // This will only show loader after 3 seconds
        return config;
    });

    // Add response interceptor
    api.interceptors.response.use(
        response => {
            if (response.config.requestId) {
                stopLoader(response.config.requestId);
            }
            return response;
        },
        error => {
            if (error.config && error.config.requestId) {
                stopLoader(error.config.requestId);
            }
            return Promise.reject(error);
        }
    );
};

// Auth Services
export const authService = {
    login: (credentials) => axios.post(`${API_URL}/auth/login`, credentials),
    logout: () => axios.post(`${API_URL}/auth/logout`),
    forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot_password`, { email }),
    resetPassword: (token, password, confirmPassword) => axios.post(`${API_URL}/auth/reset_password/${token}`, { password, confirmPassword }),
    switchRole: (roleId) => axios.post(`${API_URL}/auth/switch-role`, { role_id: roleId }),
    changePassword: (oldPassword, newPassword, confirmPassword) =>
        axios.post(`${API_URL}/auth/change_password`, { oldPassword, newPassword, confirmPassword }),
    getCurrentUser: () => axios.get(`${API_URL}/auth/me`),
    signup: async (formData) => {
        return await axios.post(`${API_URL}/auth/signup`, {
            firstName: formData.first_name,
            lastName: formData.last_name,
            email: formData.email,
            password: formData.password,
            confirm_password: formData.confirm_password
        });
    },
    setNewPassword: (token, password, confirmPassword) => 
        axios.post(`${API_URL}/auth/set-password/${token}`, { 
            password, 
            confirmPassword 
        }
    ),
    magicLogin: (token) => axios.post(`${API_URL}/auth/magic-login/${token}`),
    selfSignupEmail: (email) => axios.post(`${API_URL}/auth/self-signup-email`, { email }),
    selfSignupActivate: (token, firstName, lastName) => axios.post(`${API_URL}/auth/self-signup-activate/${token}`, { firstName, lastName }),
    selfSignupValidate: (token) => axios.get(`${API_URL}/auth/self-signup-validate/${token}`),
    learnerLoginEmail: (email, slug = null) => axios.post(`${API_URL}/auth/learner-login-email`, { email, ...(slug && { slug }) }),
    learnerLoginPassword: (email, password) => axios.post(`${API_URL}/auth/learner-login-password`, { email, password }),
    forgotPasswordCheck: (email) => axios.post(`${API_URL}/auth/forgot_password/check`, { email }),
    forgotPasswordNew: (email) => axios.post(`${API_URL}/auth/forgot_password`, { email, newCard:true }),
    selfUserCapCheck: (slug) => axios.post(`${API_URL}/auth/user_cap`, { slug }),
};

export const getTrialStatus = () => axios.get('/api/auth/trial-status');
// Dashboard Services
export const dashboardService = {
    getDashboardData: () => axios.get(`${API_URL}/dashboard`),
    getNotifications: () => axios.get(`${API_URL}/notifications`),
    getTeamStats: (startDate, endDate) => axios.get(`${API_URL}/dashboard/team-stats`, { params: { startDate, endDate } }),
    getTeamSummary: (startDate, endDate) => axios.get(`${API_URL}/dashboard/team-summary`, { params: { startDate, endDate } }),
    getCourseData: (startDate, endDate, dataType) => axios.get(`${API_URL}/dashboard/course-data`, { params: { startDate, endDate, dataType } })
};

// Content Services
export const contentService = {
    createContent: (data) => axios.post(`${API_URL}/contents`, data),
    updateContent: (id, data) => axios.put(`${API_URL}/contents/${id}`, data),
    deleteContent: (id) => axios.delete(`${API_URL}/contents/${id}`),
    getContentsList: (params) => axios.post(`${API_URL}/contents/contentsList`, params),
    getContentById: (id) => axios.get(`/api/contents/form/${id}`),
    saveContent: (formData) => axios.post('/api/contents/save', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getAllSkills: () => axios.get('/api/contents/skills'),
    exportContentsCsv: () => axios.get('/api/contents/export-csv', {
        responseType: 'blob'
    }),
    importContentSkills: (formData) => axios.post('/api/contents/import-skills', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getAIProcessingStats: () => axios.get('/api/contents/ai-processing-stats'),
    processFiles: (limit) => axios.post('/api/contents/process-files', { limit }),
    processSpecificFile: (contentId) => axios.post(`/api/contents/process-file/${contentId}`),
    processAI: (limit) => axios.post('/api/contents/process-ai', { limit }),
    processComplete: (fileLimit, aiLimit) => axios.post('/api/contents/process-complete', { fileLimit, aiLimit }),
    processAIForContent: (contentId) => axios.post(`/api/contents/process-ai/${contentId}`),
    processCompleteForContent: (contentId) => axios.post(`/api/contents/process-complete/${contentId}`),
    processCompleteBatch: (limit) => axios.post('/api/contents/process-complete-batch', { limit }),
    getLimits: () => axios.get('/api/contents/limits'),
    checkBatchAllowance: (selectedCount, selectedBytes = 0) => axios.post('/api/contents/batch-allowance', { selectedCount, selectedBytes }),
    getBatchIngestStatus: (id, config = {}) => axios.get(`/api/contents/batch/${id}/status`, config),
    resolveBatchDuplicate: (id, choice, duplicateContentId) => axios.post(`/api/contents/batch/${id}/duplicate-decision`, { choice, duplicateContentId }),
    saveBatchMetadata: (id, data) => axios.put(`/api/contents/batch/${id}/metadata`, data),
    retryBatchContent: (id) => axios.post(`/api/contents/batch/${id}/retry`),
    getBatchQuestions: (contentIds) => axios.post('/api/contents/batch-questions', { contentIds }),
    // withText false: the pop-up already has its name and description, so the server makes no AI call (K21.2 pt 46.1 (a)).
    getBatchCourseDraft: (contentIds, { withText = true } = {}) => axios.post('/api/contents/batch-course', { contentIds, withText }),
    removeBatchContent: (id) => axios.delete(`/api/contents/batch/${id}`),
    generateAnswerFromContent: (id, query) => axios.post(`/api/contents/${id}/generate-answer`, { query }),
};

// User Services
export const userService = {
    getProfile: () => axios.get(`${API_URL}/users/profile`),
    updateProfile: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/users/profile`, formData, config);
    },
    deleteProfilePhoto: () => axios.post(`${API_URL}/users/profile/delete-photo`),
    getUsers: (page, limit, searchTerm, statusFilter, audienceType) => axios.get(`${API_URL}/users`, { params: { page, limit, searchTerm, status: statusFilter, audience_type: audienceType } }),
    searchUsers: (search) => axios.get(`${API_URL}/users/search`, { params: { search } }),
    getUserDetails: (userId) => axios.get(`${API_URL}/users/view/${userId}`, {
        params: { _t: Date.now() }
    }),
    createUser: (userData) => axios.post(`${API_URL}/users/create`, userData),
    updateUser: (userId, userData) => axios.post(`${API_URL}/users/edit/${userId}`, userData),
    resetPassword: (userId, passwordData) => axios.post(`${API_URL}/users/reset-password/${userId}`, passwordData),
    // POST (K13 pt 10): these change data. Removed 12 Oct 2026 (K13 pt 10): calling them with GET.
    changeUserStatus: (userId, action) => axios.post(`${API_URL}/users/${action}/${userId}`),
    deleteUser: (userId) => axios.post(`${API_URL}/users/delete/${userId}`),
    exportUserData: () => axios.get(`${API_URL}/users/export-userdata`, { responseType: 'blob' }),
    importUsers: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/users/import-users`, formData, config);
    },
    getRoles: () => axios.get(`${API_URL}/users/roles`),
    assignRole: (userIds, roleId) => axios.post(`${API_URL}/users/bulk/assign-role`, { userIds, roleId }),
    removeRole: (userIds, roleId) => axios.post(`${API_URL}/users/bulk/remove-role`, { userIds, roleId }),
    bulkResendInvite: (userIds) => axios.post(`${API_URL}/users/bulk/resend-invite`, { userIds }),
    getUserRoles: (userId) => axios.get(`${API_URL}/users/roles/${userId}`),
    bulkDeleteUsers: (userIds) => axios.post(`${API_URL}/users/bulk/delete`, { userIds }),
    getManagerTeam: (managerId, nominationId) =>
        axios.get(`${API_URL}/users/manager-team/${managerId}/${nominationId}`),
    getDeletedUsers: (start, length, searchTerm) =>
        axios.get(`${API_URL}/users/deleted`, {
            params: { start, length, searchTerm }
        }),
    purgeUsers: (userIds) =>
        axios.post(`${API_URL}/users/purge`, { userIds }),
    restoreUsers: (userIds) => axios.post(`${API_URL}/users/restore-users`, { userIds }),
    exportSelectedUsers: (userIds) => {
        return axios.post(`${API_URL}/users/export-selected`, { userIds }, { responseType: "blob" });
    },
    getJobRoles: () => axios.get(`${API_URL}/users/job-roles`),
    getAvailableSkills: () => axios.get(`${API_URL}/users/skills/available`),
    getUserSkills: (userId = null) => {
        const endpoint = userId ? `/users/skills/${userId}` : '/users/skills/user';
        return axios.get(`${API_URL}${endpoint}`);
    },
    saveUserSkills: (skillIds) => axios.post(`${API_URL}/users/skills/save`, { skillIds }),
    hasUserSkills: () => axios.get(`${API_URL}/users/skills/check`),
    enableAuthorRole: () => axios.post(`${API_URL}/users/enable-author`),
};

// Group Services
export const groupService = {
    getGroups: (params) => axios.get(`${API_URL}/groups`, { params }),
    createGroup: (data) => axios.post(`${API_URL}/groups/create-group`, data),
    updateGroup: (id, data) => axios.post(`${API_URL}/groups/update-group/${id}`, data),
    deleteGroup: (id) => axios.delete(`${API_URL}/groups/delete-group/${id}`),
    getGroupUsers: (groupId) => axios.get(`${API_URL}/groups/group-users/${groupId}`),
    searchMembers: (term) => axios.get(`${API_URL}/groups/search-members`, { params: { q: term } }),
    getGroupMembers: (groupId) => axios.get(`${API_URL}/groups/edit-group/${groupId}`),
    exportGroupData: (groupId) => axios.get(`${API_URL}/groups/export-group-users/${groupId}`, { responseType: 'blob' }),
    exportAllGroups: (params) => axios.get(`${API_URL}/groups/export-group-users`, {
        params,
        responseType: 'blob'
    }),
    exportSelectedGroups: (groupIds) => {
        return axios.post(`${API_URL}/groups/export-selected`, { groupIds }, { responseType: "blob" });
    },
    importAssignUsers: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/groups/import-assign-users`, formData, config);
    },
    bulkDeleteGroups: (groupIds) => axios.post(`${API_URL}/groups/bulk/delete`, { groupIds }),
    getGroupCourses: (groupId) =>  axios.get(`${API_URL}/groups/${groupId}/courses`), 
    searchRequiredCourses: (term) => axios.get(`${API_URL}/groups/required-courses`, { params: { term } }),
    //previewEnrolment: (groupId, userIds) => axios.post(`${API_URL}/groups/${groupId}/enrolment-preview`, { userIds })
    previewEnrolment: (memberIds, courseIds) =>
    axios.post(`${API_URL}/groups/enrolment-preview`, {
        memberIds,
        courseIds
    })
};

// active fields services
export const activeFieldService = {
    getActiveFields: (params) => axios.get(`${API_URL}/active-fields`, { params }),
    createActiveField: (data) => axios.post(`${API_URL}/active-fields/create`, data),
    updateActiveField: (id, data) => axios.post(`${API_URL}/active-fields/edit/${id}`, data),
    deleteActiveField: (id) => axios.delete(`${API_URL}/active-fields/delete/${id}`),
    getActiveFieldById: (id) => axios.get(`${API_URL}/active-fields/edit/${id}`),
};

export const courseService = {
    getCourses: (page, limit, search, sortOrder, authorId, userId, audienceType) => {
        return axios.get(`${API_URL}/courses`, { params: { page, limit, search, sort: sortOrder, author: authorId, user_id: userId, audience_type: audienceType } });
    },
    getCourseById: (id) => {
        return axios.get(`${API_URL}/courses/form/${id}`, { timeout: 15000 });
    },
    createCourse: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/courses/create`, formData, config);
    },
    updateCourse: (id, formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.put(`${API_URL}/courses/update/${id}`, formData, config);
    },
    getComponentsList: async (params) => {
        return await axios.post(`${API_URL}/courses/components-list`, {
            draw: params.draw || 1,
            start: params.start || 0,
            length: params.length || 10,
            columns: [
                { search: { value: '' } },
                { search: { value: '' } },
                { search: { value: params.subType || '' } },
                { search: { value: params.type || '' } }
            ],
            search: { value: params.search || '' },
            course_id: params.courseId,
            audience_type: params.audienceType
        });
    },
    getSettings: (id) => axios.get(`${API_URL}/courses/settings/${id}`),
    updatePrerequisitesOrdering: (formData) => {
        return axios.post(`${API_URL}/courses/update-order-prerequisite`, formData);
    },
    // updateSettings: (id, settings) => axios.put(`${API_URL}/courses/settings/${id}`, settings),
    updateSettings: (id, settings, config = {}) => {
        return axios.put(`${API_URL}/courses/settings/${id}`, settings, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            ...config,
        });
    },
    deleteAttachment :async (id) => {
    return await axios.delete(`${API_URL}/courses/attachments/${id}`);
    },
    getAuthors: (term) => axios.get(`${API_URL}/courses/authors`, {
        params: { term }
    }),
    searchPrerequisites: (courseId, searchText) => axios.post(`${API_URL}/courses/prerequisites-list`, {
        draw: 1,
        start: 0,
        length: 10,
        search: { value: searchText },
        course_id: courseId
    }),

    getInstance: (courseId) => axios.get(`${API_URL}/courses/instances/${courseId}`),
    updateInstance: (instanceId, data) => axios.post(`${API_URL}/courses/update-instance/${instanceId}`, data),
    addNewInstance: (instanceData) => axios.post(`${API_URL}/courses/add-instance`, instanceData),
    deleteInstance: (instanceId) => axios.post(`${API_URL}/courses/delete-instance`, { instance_id: instanceId }),
    publishCourse: (id) => axios.post(`${API_URL}/courses/publish/${id}`),
    retireCourse: (id) => axios.post(`${API_URL}/courses/retire/${id}`),
    deleteCourse: (id) => axios.post(`${API_URL}/courses/delete/${id}`),
    duplicateCourse: (id) => axios.post(`${API_URL}/courses/duplicate/${id}`),
    getFeedbackSettings: (itemId, instanceId, isLearningPath = false) => {
        const endpoint = isLearningPath ?
            `${API_URL}/settings/lpath-feedback-settings/${itemId}/${instanceId}` :
            `${API_URL}/settings/feedback-settings/${itemId}/${instanceId}`;
        return axios.get(endpoint);
    },
    saveFeedbackSettings: (itemId, instanceId, data, isLearningPath = false) => {
        const endpoint = isLearningPath ?
            `${API_URL}/settings/lpath-feedback-settings/${itemId}/${instanceId}` :
            `${API_URL}/settings/feedback-settings/${itemId}/${instanceId}`;
        const formattedData = {
            enabled: data.enabled ? 1 : 0,
            questionRequirement: data.questionRequirement,
            question1: data.question1,
            question2: data.question2,
            q1Mandatory: data.q1Mandatory ? 1 : 0,
            q2Mandatory: data.q2Mandatory ? 1 : 0,
            showImmediate: data.showImmediate ? 1 : 0
        };

        return axios.post(endpoint, formattedData);
    },
    getCourseFeedbackSettings: (itemId, isLearningPath = false) => {
        const endpoint = isLearningPath ?
            `${API_URL}/settings/lpath-feedback-settings/${itemId}` :
            `${API_URL}/settings/feedback-settings/${itemId}`;
        return axios.get(endpoint);
    },
    saveCourseFeedbackSettings: (itemId, data, isLearningPath = false) => {
        const endpoint = isLearningPath ?
            `${API_URL}/settings/lpath-feedback-settings/${itemId}` :
            `${API_URL}/settings/feedback-settings/${itemId}`;
        return axios.post(endpoint, data);
    },
    updateInstanceDate: (id) => axios.post(`${API_URL}/courses/update-instance/${id}`),

    getCourseLearners: (courseId, params) =>
        axios.post(`${API_URL}/courses/get-learners`, {
            ...params,
            course_id: courseId
        }),
    getEnrollForm: (courseId) => {
        return axios.get(`${API_URL}/courses/enroll-form/${courseId}`);
    },
    searchMembers: (term, courseId) => axios.get(`${API_URL}/courses/search-members`, {
        params: { term, course_id: courseId }
    }),
    enrollLearners: (data) => axios.post(`${API_URL}/courses/enroll-learners`, data),
    unenrollLearner: (learnerId) => axios.post(`${API_URL}/courses/unenroll-learner`, { learner_id: learnerId }),
    markLearnerCompletion: (learnerId) => axios.post(`${API_URL}/courses/mark-learner-completion`, { learner_id: learnerId }),
    resetLearnerModules: (learnerId) => axios.post(`${API_URL}/courses/reset-learner-modules`, { learner_id: learnerId }),
    exportLearnersData: (courseId, instanceId, searchTerm) => axios.get(`${API_URL}/courses/get_learners_list`, {
        params: { course_id: courseId, instance_id: instanceId, search_term: searchTerm },
        responseType: 'blob'
    }),
    getCourseDetails: (id) => {
        return axios.get(`${API_URL}/courses/details/${id}`);
    },
    bulkUnenrollLearners: (learnerIds) => axios.post(`${API_URL}/courses/bulk-unenroll-learners`, { learner_ids: learnerIds }),
    bulkMarkLearnerCompletion: (learnerIds) => axios.post(`${API_URL}/courses/bulk-mark-learner-completion`, { learner_ids: learnerIds }),
    bulkResetLearnerModules: (learnerIds) => axios.post(`${API_URL}/courses/bulk-reset-learner-modules`, { learner_ids: learnerIds }),
    getL1Feedback: (courseId) => axios.get(`${API_URL}/courses/l1-feedback/${courseId}`),
    getL1FeedbackList: (courseId, params) => axios.post(`${API_URL}/courses/get-feedback-list`, {
        ...params,
        courseId
    }),
    exportL1Feedback: (courseId, instanceId) => axios.get(`${API_URL}/courses/export-feedback`, {
        params: { courseId, instanceId },
        responseType: 'blob'
    }),
    getL1FeedbackDetails: (feedbackId) => axios.get(`${API_URL}/courses/l1-feedback-details/${feedbackId}`),
    getCoursesList: (params) => axios.get(`${API_URL}/courses`, { params }),
    /*
    getCoursePreview: (courseId, instanceId = 0) =>
        axios.get(`${API_URL}/learner/course/${courseId}/${instanceId}`, {
            headers: { 'Accept': 'application/json' }
        }),
    */
    getCoursePreview: (courseId) => axios.get(`${API_URL}/learner/course/${courseId}`, {headers: { 'Accept': 'application/json' }}),
    getNominationDetails: (nominationId) => axios.get(`${API_URL}/courses/nomination-details/${nominationId}`),
    getManagerTeam: (managerId, nominationId) =>
        axios.get(`${API_URL}/users/manager-team/${managerId}/${nominationId}`),
    submitNominations: (nominationId, learnerIds) => axios.post(`${API_URL}/courses/submit-nominations`, {
        nominationId,
        learnerIds
    }),
    acceptNomination: (nominationId) => axios.post(`${API_URL}/courses/accept-nomination`, { nominationId }),
    rejectNomination: (nominationId) => axios.post(`${API_URL}/courses/reject-nomination`, { nominationId }),

    // Track content progress (for videos, etc.)
    trackContentProgress: (ccrId, courseId, progress) =>
        axios.post(`${API_URL}/courses/content/progress`, {
            clr_id: ccrId,
            course_id: courseId,
            progress
        }),

    // Get content completion status
    getContentCompletionStatus: (courseId) => axios.get(`${API_URL}/courses/${courseId}/completion-status`),

    updateCourseContentOrder: (courseId, orderedList) => {
        return axios.post(`${API_URL}/courses/content-order`, { courseId, orderedList });
    },
    getBookmarks: () => axios.get(`${API_URL}/learner/bookmarks`),
    addBookmark: (courseId) => axios.post(`${API_URL}/learner/bookmark-add`, { courseId }),
    removeBookmark: (courseId) => axios.delete(`${API_URL}/learner/${courseId}/bookmark-remove`),
    // getFavorites: () => axios.get(`${API_URL}/learner/favorites`),
    getFavorites: (params) => axios.get(`${API_URL}/learner/favorites`, { params }),
};

export const settingsService = {
    getFeedbackSettings: () => axios.get(`${API_URL}/settings/feedback`),
    updateFeedbackSettings: (data) => axios.post(`${API_URL}/settings/feedback`, data),
    getEmailTemplates: () => {
        return axios.get(`${API_URL}/settings/defaultEmails`, {
            params: { _t: Date.now() }
        });
    },
    getLearningActivityTemplates: () => axios.get(`${API_URL}/settings/learning-activity`),
    getRemindersTemplates: () => axios.get(`${API_URL}/settings/remainders-Updates`),
    getEmailTemplate: (id) => axios.get(`${API_URL}/settings/approval-form/${id}`),
    updateEmailTemplate: (data) => axios.post(`${API_URL}/settings/approval-form`, data),
    updateEmailStatus: (data) => axios.post(`${API_URL}/settings/update-status`, data),
    getEmailConfiguration: () => axios.get(`${API_URL}/settings/emailConfiguration`),
    updateEmailConfiguration: (data) => axios.post(`${API_URL}/settings/update`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    getCourseEmailTemplates: (courseId) => axios.get(`${API_URL}/settings/email-template/${courseId}`),
    updateCourseEmailTemplateStatus: (data) => axios.post(`${API_URL}/settings/email-template`, data),
    getLearningPathEmailTemplates: (lpathId) => axios.get(`${API_URL}/settings/lp-email-template/${lpathId}`),
    updateLearningPathEmailTemplateStatus: (data) => axios.post(`${API_URL}/settings/lp-email-template`, data),
    updateEmailTemplates: (data) => axios.post(`${API_URL}/settings/update-email-template`, data),
    getGeneralSettings: (data) => axios.get(`${API_URL}/settings/general-settings`, data),
    updateGeneralSettings: (data) => axios.post(`${API_URL}/settings/general-settings`, data),

    // Get embed settings
    getEmbedSettings: () => axios.get(`${API_URL}/settings/embed`),

    // Update embed settings  
    updateEmbedSettings: (settings) => axios.put(`${API_URL}/settings/embed`, settings),

    // Initialize embed settings (optional - for first time setup)
    initializeEmbedSettings: () => axios.post(`${API_URL}/settings/embed/initialize`),

    // Embed token methods
    getEmbedToken: () => axios.get(`${API_URL}/settings/embed-token`),
    generateEmbedToken: (data) => axios.post(`${API_URL}/settings/embed-token/generate`, data),
    updateEmbedToken: (data) => axios.put(`${API_URL}/settings/embed-token`, data),
    revokeEmbedToken: () => axios.delete(`${API_URL}/settings/embed-token`),
    // Removed 11 Oct 2026 (K13): validateEmbedToken, unused, which called /embed/validate-token without /api.
    getLogo: () => axios.get(`${API_URL}/settings/logo`),
    uploadLogo: (formData) => 
    axios.post(`${API_URL}/settings/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getAllEmailTemplates: () => axios.get(`${API_URL}/settings/all-emailtemplates`)

};
export const assignmentService = {
    getAssignmentsList: (params) => axios.post(`${API_URL}/assignments/assignmentsList`, params),
    getAssignment: (id) => axios.get(`${API_URL}/assignments/form/${id}`),
    createAssignment: (data) => axios.post(`${API_URL}/assignments/save`, data),
    updateAssignment: (id, data) => axios.put(`${API_URL}/assignments/save`, { ...data, id }),
    deleteAssignment: (id) => axios.post(`${API_URL}/assignments/remove/${id}`),
    getAssignmentPreview: (assignmentId, courseId, ccrId) => axios.get(`${API_URL}/assignments/preview/${assignmentId}/${courseId}/${ccrId}`),
    submitAssignment: (assignment_id, courseId, ccrId) => axios.get(`${API_URL}/assignments/submit/${assignment_id}/${courseId}/${ccrId}`),
    saveSubmission: (assignment_id, formData = {}) => axios.post(`${API_URL}/assignments/submit_assignment/${assignment_id}`, formData),
    getAssignmentSubmission: (params) => axios.post(`${API_URL}/assignments/instructor_submissions_check`, params),
    getAssignmentRank: (assignment_id, course_id) => axios.get(`/api/assignments/assignment_rank/${assignment_id}/${course_id}`),
    getAssignmentLearnersList: (params) => axios.post(`${API_URL}/assignments/assignment_learners_list`, params),
    getStudentGrade: (assignment_id, learner_id, course_id) => axios.get(`/api/assignments/learner_grade/${assignment_id}/${learner_id}/${course_id}`),
    updateStudentGrade: (params) => axios.post(`${API_URL}/assignments/update_grade`, params),
    checkAssignmentName: (name, excludeId = null) => {
    return axios.get('/api/assignments/check-name', {
        params: { name, excludeId }
    });
    }

};
export const questionService = {
    getQuestionsList: (params) => axios.post(`${API_URL}/questions/questionsList`, params),
    getQuestionType: (id) => axios.get(`${API_URL}/questions/form/${id}`),
    createQuestion: (data) => axios.post(`${API_URL}/questions/save`, data),
    updateQuestion: (id, data) => axios.put(`${API_URL}/questions/save`, { ...data, id }),
    deleteQuestion: (id) => axios.post(`${API_URL}/questions/remove/${id}`),
    getQuestionPreview: (questionId, params = {}) => axios.get(`${API_URL}/questions/preview/${questionId}`),
    getQuestionOptions: (data) => axios.post(`${API_URL}/questions/options`, data),
};
export const quizService = {
    getQuizList: (params) => axios.post(`${API_URL}/quiz/quizList`, params),
    getQuiz: (id) => axios.get(`${API_URL}/quiz/form/${id}`),
    createQuiz: (data) => axios.post(`${API_URL}/quiz/save`, data),
    updateQuiz: (id, data) => axios.put(`${API_URL}/quiz/save`, { ...data, id }),
    deleteQuiz: (id) => axios.post(`${API_URL}/quiz/remove/${id}`),
    getQuestionsList: (params) => axios.post(`${API_URL}/quiz/questions_list`, params),
    getAddQuestion: (data) => axios.post(`${API_URL}/quiz/add_questions`, data),
    updateQuizStatus: (data) => axios.post(`${API_URL}/quiz/set-status`, data),
    deleteQuestion: (refid) => axios.post(`${API_URL}/quiz/rm_question`, { refid }),
    getQuizPreview: (quizId, courseId, ccrId) => axios.get(`${API_URL}/quiz/preview/${quizId}/${courseId}/${ccrId}`),
    getStartQuiz: (quizId, courseId, ccrId) => axios.get(`${API_URL}/quiz/start/${quizId}/${courseId}/${ccrId}`),
    getLoadQuestions: ({ quiz_id, course_id, ccr_id, question_cnt, question_id, ...params }) => {
        const queryParams = new URLSearchParams({
            quiz_id: quiz_id,
            course_id: course_id,
            ccr_id: ccr_id,
            question_cnt: question_cnt,
            question_id: question_id,
            ...params
        }).toString();
        return axios.get(`${API_URL}/quiz/question?${queryParams}`);
    },
    getSavedAnswer: (data) => axios.post(`${API_URL}/quiz/save-answer`, data),
    submitQuiz: (data) => axios.post(`${API_URL}/quiz/submit`, data),
    getResultQuiz: (quizId) => axios.get(`/api/quiz/result/${quizId}`),
    getResultDetails: (quiz_id) => axios.get(`/api/quiz/result-details/${quiz_id}`),
    getQuizSubmission: (params) => axios.post(`${API_URL}/quiz/instructor_submissions_quiz_check`, params),
    getQuizRank: (quiz_id, course_id) => axios.get(`/api/quiz/quiz_rank/${quiz_id}/${course_id}`),
    getQuizLearnersList: (params) => axios.post(`${API_URL}/quiz/quiz_learners_list`, params),
    getStudentGrade: (quiz_id, learner_id, course_id) => axios.get(`/api/quiz/learner_grade/${quiz_id}/${learner_id}/${course_id}`),
    updateStudentGrade: (params) => axios.post(`${API_URL}/quiz/update_grade`, params),
    saveQuestionOrder: (quizId, questions) => axios.post(`${API_URL}/quiz/save-question-order/${quizId}`, { questions }),
    getQuizQuestions: (quizId) => axios.get(`${API_URL}/quiz/get_questions/${quizId}`)


};

export const learningPathService = {
    getLPaths: (page, limit, search, sortOrder, authorId, userId, audienceType) => {
        return axios.get(`${API_URL}/learning_path`, { params: { page, limit, search, sort: sortOrder, author: authorId, user_id: userId, audience_type: audienceType } });
    },
    getLPathById: (id) => {
        return axios.get(`${API_URL}/learning_path/form/${id}`, { timeout: 15000 });
    },
    getLPDetails: (id) => {
        return axios.get(`${API_URL}/learning_path/details/${id}`, { timeout: 15000 });
    },
    getLPathLearners: (lpathId, params) =>
        axios.post(`${API_URL}/learning_path/get_learners_list`, {
            ...params,
            lpath_id: lpathId
        }),
    searchMembers: (term, lpathId) => axios.get(`${API_URL}/learning_path/search-members`, {
        params: { term, lpath_id: lpathId }
    }),
    enrollLearners: (data) => axios.post(`${API_URL}/learning_path/enroll-learners`, data),
    unenrollLearner: (lpathId, learnerId) => axios.post(`${API_URL}/learning_path/unenroll/${lpathId}/${learnerId}`),
    markLearnerCompletion: (lpathId, learnerId) => axios.post(`${API_URL}/learning_path/mark_completion/${lpathId}/${learnerId}`),
    resetLearnerProgress: (lpathId, learnerId) => axios.post(`${API_URL}/learning_path/reset_progress/${lpathId}/${learnerId}`),
    exportLearnersData: (lpathId, instanceId, contentId, searchTerm) => axios.get(`${API_URL}/learning_path/download_learners_list`, {
        params: {
            lpath_id: lpathId,
            instance_id: instanceId,
            content_id: contentId,
            search_term: searchTerm
        },
        responseType: 'blob'
    }),
    bulkUnenrollLearners: (lpathId, learnerIds) => axios.post(`${API_URL}/learning_path/bulk-unenroll/${lpathId}`, { learnerIds }),
    bulkMarkLearnerCompletion: (lpathId, learnerIds) => axios.post(`${API_URL}/learning_path/bulk-mark-completion/${lpathId}`, { learnerIds }),
    bulkResetLearnerProgress: (lpathId, learnerIds) => axios.post(`${API_URL}/learning_path/bulk-reset-progress/${lpathId}`, { learnerIds }),
    createLPath: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/learning_path/create`, formData, config);
    },
    updatelpath: (id, formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.put(`${API_URL}/learning_path/create/${id}`, formData, config);
    },
    lpathComponentsList: async (params) => {
        return await axios.post(`${API_URL}/learning_path/components_list`, params);
    },
    getSettings: (id) => axios.get(`${API_URL}/learning_path/settings/${id}`),
    updateSetting: (id, settings, config = {}) => {
        console.log('API call to update settings:', id, settings);
        return axios.put(`${API_URL}/learning_path/settings/${id}`, settings, config);
    },
    updateSettings: (lpathId, data) => {
        return axios.post(`${API_URL}/learning_path/contents/${lpathId}`, data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },

    searchPrerequisites: (lpathId, searchText) => axios.post(`${API_URL}/learning_path/prerequisites-list`, {
        draw: 1,
        start: 0,
        length: 10,
        search: { value: searchText },
        lpath_id: lpathId
    }),
    getInstance: (lpathId) => axios.get(`${API_URL}/learning_path/instances/${lpathId}`),
    updateInstance: (instanceId, data) => axios.post(`${API_URL}/learning_path/update-instance/${instanceId}`, data),
    addNewInstance: (instanceData) => axios.post(`${API_URL}/learning_path/add-instance`, instanceData),
    deleteInstance: (instanceId) => axios.post(`${API_URL}/learning_path/delete-instance`, { instance_id: instanceId }),
    updateCoursesInstances: (instanceId, data) => {
        return api.post(`/learning_path/update_courses_instance/${instanceId}`, data);
    },

    publishLearningPath: (id) => axios.post(`${API_URL}/learning_path/publish/${id}`),
    retireLearningPath: (id) => axios.post(`${API_URL}/learning_path/retire/${id}`),
    deleteLearningPath: (id) => axios.post(`${API_URL}/learning_path/delete/${id}`),
    duplicateLearningPath: (id) => axios.post(`${API_URL}/learning_path/duplicate/${id}`),

    getL1Feedback: (lpathId) => axios.get(`${API_URL}/learning_path/l1-feedback/${lpathId}`),
    getL1FeedbackList: (lpathId, params) => axios.post(`${API_URL}/learning_path/get-feedback-list`, {
        ...params,
        lpathId
    }),
    getL1FeedbackDetails: (feedbackId) => axios.get(`${API_URL}/learning_path/l1-feedback-details/${feedbackId}`),
    exportL1Feedback: (lpathId, instanceId) => axios.get(`${API_URL}/learning_path/export-feedback`, {
        params: { lpathId, instanceId },
        responseType: 'blob'
    }),
    getComponents: (lpathId, offset = 0, limit = 12, searchTerm = '') => {
        return axios.post(`${API_URL}/learning_path/components_list`, {
            lpathId,
            offset,
            limit,
            searchTerm
        });
    },
    addContent: (data) => {
        return axios.post(`${API_URL}/learning_path/add-content`, data);
    },
    updateOrder: (lpathId, elements) => {
        const payload = {
            lpath_id: lpathId, elements: elements.map((el, index) => ({
                lpcr_id: el.lpcr_id, sequence: index + 1, lement_type: el.type,
                element_id: el.id,
            })),
        };
        return axios.post(`${API_URL}/learning_path/update-order`, payload);
    },

    // Publish learning path (convenience method that calls updateSettings)
    publishLearningPath: (lpathId) => {
        return axios.post(`${API_URL}/learning_path/publish/${lpathId}`, {
            status: '1' // Published status
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },

    // Update content order and requirements in one call
    batchUpdateLearningPath: (lpathId, data) => {
        return axios.post(`${API_URL}/learning_path/contents/${lpathId}`, data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },

    removeContent: (data) => {
        return axios.post(`${API_URL}/learning_path/remove-content`, data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },

    // Switch to a different learning path instance
    switchInstance: (lpathId, instanceId) => {
        return axios.post(`${API_URL}/learner/lpath/switch-instance/${lpathId}/${instanceId}`);
    },

    // Update course instance within a learning path
    updateCourseInstance: (lpathId, contentId, contentType, instanceId) => {
        return axios.post(`${API_URL}/learner/lpath/flexi-update/${lpathId}`, {
            contentId,
            contentType,
            instanceId
        });
    },
    updatePrerequisitesOrdering: (formData) => {
        return axios.post(`${API_URL}/learning_path/update-order-prerequisite`, formData);
    },
    getAuthors: (searchTerm) => axios.get(`${API_URL}/learning_path/settings-authors`, {
        params: { search: searchTerm }
    }),
    deleteAttachment: (attachmentId) => axios.delete(`${API_URL}/learning_path/attachment/${attachmentId}`),
    getContentLockStatus: (lpathId, learnerId) => {
    return axios.get(`${API_URL}/learning_path/content-lock-status?lpath_id=${lpathId}&learner_id=${learnerId}`);
  }
};

export const notificationService = {
    getNotifications: (tab = 'all') => axios.post(`${API_URL}/notifications/list`, { tab }),
    getLatestNotifications: () => axios.get(`${API_URL}/notifications/latest`),
    getUnreadCount: () => axios.get(`${API_URL}/notifications/unread-count`),
    approve: (notificationId, courseId, learnerId) =>
        axios.post(`${API_URL}/notifications/approve-enrollment`, { notificationId, courseId, learnerId }),
    reject: (notificationId, courseId, learnerId) =>
        axios.post(`${API_URL}/notifications/reject-enrollment`, { notificationId, courseId, learnerId }),
    markAsRead: (notificationId) => axios.post(`${API_URL}/notifications/mark-read/${notificationId}`)
};

export const learnerService = {
    getCatlog: (params = {}) => {
        const queryString = Object.keys(params)
            .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&');
        return axios.get(`${API_URL}/learner/catlog${queryString ? `?${queryString}` : ''}`);
    },
    getMyLearnings: (params = {}) => {
        const queryString = Object.keys(params)
            .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&');
        return axios.get(`${API_URL}/learner/my-learnings${queryString ? `?${queryString}` : ''}`);
    },
    startCourse: (courseId) => {
        return axios.post(`${API_URL}/learner/course/start/${courseId}`);
    },
    enrollmentRequest: (courseId, instanceId) =>
        axios.post(`${API_URL}/learner/enroll-request/${courseId}/${instanceId}`),
    // Enrol and unenrol are POST (K13 pt 10). Removed 12 Oct 2026 (K13 pt 10): calling them with GET.
    enrollInCourse: (courseId, instanceId = '') => {
        return axios.post(`${API_URL}/learner/enroll/${courseId}/${instanceId}`, null, {
            headers: {
                'Accept': 'application/json'
            }
        });
    },
    selfUnenroll: (courseId, instanceId) => {
        return axios.post(`${API_URL}/learner/self-unenroll/${courseId}/${instanceId}`, null, {
            headers: {
                'Accept': 'application/json'
            }
        });
    },
    // Its route is removed (K13 pt 10), so this call now fails. It and the Switch Instance button in CourseEnrollment go
    // with N40.1 pt 15, which replaces that page's own button logic.
    switchInstance: (courseId, instanceId) => {
        return axios.get(`${API_URL}/learner/switch-instance/${courseId}/${instanceId}`, {
            headers: {
                'Accept': 'application/json'
            }
        });
    },
    updateScormStatus: (courseId, ccrId, statusData) =>
        axios.get(`${API_URL}/learner/scorm-status/${courseId}/${ccrId}`, {
            params: statusData
        }),
    // POST (K13 pt 10); selectedInstances stays in the address, where the server reads it.
    enrollInLearningPath: (lpathId, instanceId, selectedInstances = []) => axios.post(`${API_URL}/learner/lpath/enroll/${lpathId}/${instanceId}`, null, {
        params: { selectedInstances: JSON.stringify(selectedInstances) }, headers: { 'Accept': 'application/json' }
    }),
    selfunenrollFromLearningPath: (lpathId) =>
        axios.post(`${API_URL}/learner/lpath/unenroll/${lpathId}`, null, {
            headers: { 'Accept': 'application/json' }
        }),
    startLearningPath: (lpathId, enrollmentId, firstContentId = null) => axios.post(`${API_URL}/learner/lpath/start/${lpathId}`, {
        enrollmentId, firstContentId
    }),
    // Add the corrected recommendation methods
    getSkillBasedRecommendations: (limit = 5) => {
        return axios.get(`${API_URL}/learner/recommendations/skills`, {
            params: { limit }
        });
    },

    getAllSkillBasedRecommendations: (page = 1, limit = 10, type = 'all') => {
        return axios.get(`${API_URL}/learner/recommendations/skills/all`, {
            params: { page, limit, type }
        });
    },
    getRecentCourses: () => axios.get(`${API_URL}/learner/recent-courses`),
    //getDueComplianceCourse: () => axios.get(`${API_URL}/learner/compliance-due`),
    getComplianceCourses: () => axios.get(`${API_URL}/learner/compliance-courses`),
    //getMandatoryCourse: () => axios.get(`${API_URL}/learner/mandatory`),
    getSkillCategoryRecommendation: () => axios.get(`${API_URL}/learner/skill-recommendation`),
    getQuickSkillContent: () => axios.get(`${API_URL}/learner/quick-skill-content`),
    // Accept either a type string (e.g. 'video') or a params object
    getRecentContent: (typeOrParams = {}) => {
        let params = {};
        if (typeof typeOrParams === 'string') {
            if (typeOrParams && typeOrParams !== 'all') params.type = typeOrParams;
        } else if (typeOrParams && typeof typeOrParams === 'object') {
            params = { ...typeOrParams };
            if (params.type === 'all') delete params.type;
        } else if (typeof typeOrParams === 'object' && typeOrParams.search) {
            params = { ...typeOrParams };
        }

        return axios.get(`${API_URL}/learner/recent-contents`, { params });
    },
    //() => axios.get(`${API_URL}/learner/recent-contents`),
    getContentRatings: (contentId) => axios.get(`${API_URL}/learner/content-ratings/${contentId}`),
    submitContentRating: (contentId, rating, comment) =>
        axios.post(`${API_URL}/learner/submit-rating`, { contentId, rating, comment })
};

export const feedbackService = {
    getFeedbackQuestions: (itemId, enrollmentId, itemType) =>
        axios.get(`${API_URL}/feedback/get-modal/${itemId}/${enrollmentId}/${itemType}`),

    submitFeedback: (courseId, enrollmentId, instanceId, answers) =>
        axios.post(`${API_URL}/feedback/submit`, { courseId, enrollmentId, instanceId, answers }),

    submitLPathFeedback: (lpathId, enrollmentId, instanceId, answers) =>
        axios.post(`${API_URL}/feedback/submit-lpath`, { lpathId, enrollmentId, instanceId, answers })
};

export const skillService = {
    getSkills: (page, limit, searchTerm, categoryFilter) =>
        axios.get(`${API_URL}/skills`, { params: { page, limit, searchTerm, categoryFilter } }),
    getCategories: () => axios.get(`${API_URL}/skills/categories`),
    createSkill: (data) => axios.post(`${API_URL}/skills/create`, data),
    createCategory: (data) => axios.post(`${API_URL}/skills/create-category`, data),
    updateSkill: (id, data) => axios.post(`${API_URL}/skills/edit/${id}`, data),
    deleteSkill: (id) => axios.delete(`${API_URL}/skills/delete/${id}`),
    getSkillById: (id) => axios.get(`${API_URL}/skills/edit/${id}`),
    bulkDeleteSkills: (skillIds) => axios.post(`${API_URL}/skills/bulk/delete`, { skillIds }),
    importCategories: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/skills/import-categories`, formData, config);
    },
    importSkills: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/skills/import-skills`, formData, config);
    },
    getCategoriesList: (page, limit, searchTerm) =>
        axios.get(`${API_URL}/skills/categories-list`, { params: { page, limit, searchTerm } }),
    getCategoryById: (id) => axios.get(`${API_URL}/skills/categories/edit/${id}`),
    updateCategory: (id, data) => axios.post(`${API_URL}/skills/categories/edit/${id}`, data),
    deleteCategory: (id) => axios.delete(`${API_URL}/skills/categories/delete/${id}`),
    bulkDeleteCategories: (categoryIds) => axios.post(`${API_URL}/skills/categories/bulk/delete`, { categoryIds }),
    exportSkills: (categoryFilter, searchTerm) =>
        axios.get(`${API_URL}/skills/export`, {
            params: { categoryFilter, searchTerm },
            responseType: 'blob'
        }),
    exportCategories: (searchTerm) =>
        axios.get(`${API_URL}/skills/export-categories`, {
            params: { searchTerm },
            responseType: 'blob'
        }),
    exportSelectedSkills: (skillIds) =>
        axios.post(`${API_URL}/skills/export-selected`, { skillIds }, { responseType: 'blob' }),
    exportSelectedCategories: (categoryIds) =>
        axios.post(`${API_URL}/skills/export-selected-categories`, { categoryIds }, { responseType: 'blob' })
};

export const jobRoleService = {
    getJobRoles: (page, limit, searchTerm, statusFilter, sortBy) =>
        axios.get(`${API_URL}/job-roles`, { params: { page, limit, searchTerm, statusFilter, sortBy } }),
    createJobRole: (data) => axios.post(`${API_URL}/job-roles/create`, data),
    getJobRoleById: (id) => axios.get(`${API_URL}/job-roles/edit/${id}`),
    updateJobRole: (id, data) => axios.post(`${API_URL}/job-roles/edit/${id}`, data),
    bulkDeleteJobRoles: (jobRoleIds) => axios.post(`${API_URL}/job-roles/bulk/delete`, { jobRoleIds }),
};

export const jobAidService={
//    getJobAidsList: (page, limit, searchTerm) => axios.post(`${API_URL}/job-aids`, { params: { page, limit, searchTerm} }),
// getJobAidsList: (page, limit, searchTerm) => axios.get(`${API_URL}/job-aids`, {
//   params: {
//     page,
//     limit,
//     searchTerm
//   }
// }),
getAllJobAids: (page, limit, searchTerm) =>
  axios.get('/api/job-aids', {
    params: {
      page,
      limit,
      searchTerm,
    },
  }),

   create: (formdata) =>axios.post(`${API_URL}/job-aids/create`, formdata, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
     edit: (id, formdata) =>
    axios.post(`${API_URL}/job-aids/edit/${id}`, formdata, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    getById: (id) => axios.get(`${API_URL}/job-aids/${id}`),
    getJobAidById: (id) => axios.get(`${API_URL}/job-aids/${id}`),
    deleteJobAid: (id) => axios.post(`${API_URL}/job-aids/delete/${id}`),
    withdrawnJobAid: (id) => axios.post(`${API_URL}/job-aids/withdraw/${id}`),
    publishJobAid: (id) => axios.post(`${API_URL}/job-aids/publish/${id}`),

}

// Report service
export const reportService = {
    getLearnerProgress: (page, limit, search) => 
        axios.get(`${API_URL}/reports/learner-progress`, {
            params: {
                page,
                limit,
                search
            }
        }),
    exportLearnerProgress: (searchTerm = '') =>
        axios.get(`${API_URL}/reports/export-learner-progress`, {
            params: { searchTerm },
            responseType: 'blob'
        }),
    getSkillGraphSummary: (params) => 
        axios.get(`${API_URL}/reports/skill-graph-summary`, {
            params
        }),
    exportSkillGraphSummary: (params) =>
        axios.get(`${API_URL}/reports/skill-graph-summary/export`, {
            params,
            responseType: 'blob'
        }),
    getCourseEngagement: (params) => 
        axios.get(`${API_URL}/reports/course-engagement`, {
            params
        }),
    exportCourseEngagement: (params) =>
        axios.get(`${API_URL}/reports/course-engagement/export`, {
            params,
            responseType: 'blob'
        }),
    getJobRoleReadiness: (params) => {
        return axios.get(`${API_URL}/reports/job-role-readiness`, { params });
    },

    exportJobRoleReadiness: (params) => {
        return axios.get(`${API_URL}/reports/job-role-readiness/export`, { 
            params,
            responseType: 'blob'
        });
    },

    getDashboardStats: (params = {}) => {
        return axios.get(`${API_URL}/reports/dashboard-stats`, { params });
    },

    getDashboardData: () => {
        return axios.get(`${API_URL}/api/reports/dashboard`);
    },

    getSearchInsights: (params) => {
        return axios.get(`${API_URL}/reports/search-insights`, { params });
    },
    
    exportSearchInsights: (params) => {
        return axios.get(`${API_URL}/reports/search-insights/export`, { 
            params,
            responseType: 'blob'
        });
    },

    getSearchStats: () => {
        return axios.get(`${API_URL}/reports/search-stats`);
    },

    getComplianceReport: (params) => {
        return axios.get(`${API_URL}/reports/compliance`, { params });
    },
    
    exportComplianceReport: (params) => {
        return axios.get(`${API_URL}/reports/compliance/export`, { 
            params,
            responseType: 'blob'
        });
    },

    // The same audience and cohort as the page (N5 pt 30).
    getComplianceLearners: (courseId, type, filters = {}) => {
        return axios.get(`${API_URL}/reports/compliance/learners`, {
            params: { courseId, type, ...filters }
        });
    },

    sendOverdueEscalation: (courseId, learnerIds) => {
        return axios.post(`${API_URL}/reports/compliance/send-escalation`, {
            courseId,
            learnerIds
        });
    },

    getContentRating: (contentId) => axios.get(`${API_URL}/reports/content-rating`, { params: { contentId } }),
    getContentRatings: (contentId, page = 0, limit = 50) => axios.get(`${API_URL}/reports/content-ratings`, { params: { contentId, page, limit } }),

    getActiveUsersReport: (params) => axios.get(`${API_URL}/reports/active-users`, { params }),
    exportActiveUsersReport: (params) => axios.get(`${API_URL}/reports/active-users/export`, { params, responseType: 'blob' }),
};

export const certificateService={
    getAllCertificates(page, limit, search, sortOrder) {
    return axios.get(`${API_URL}/certificates`, {
        params: { page, limit, search, sort :sortOrder }
    });
    },
    getCertificateById: (id) => {
        return axios.get(`${API_URL}/certificates/form/${id}`);
    },
    createCertificate: (formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.post(`${API_URL}/certificates/create`, formData, config);
    },
    updateCertificate: (id, formData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        return axios.put(`${API_URL}/certificates/update/${id}`, formData, config);
    },
    getById: (id) => axios.get(`${API_URL}/certificates/${id}`),
    publishCertificate: (id) => axios.post(`${API_URL}/certificates/publish/${id}`),
    retireCertificate: (id) => axios.post(`${API_URL}/certificates/retire/${id}`),
    deleteCertificate: (id) => axios.post(`${API_URL}/certificates/delete/${id}`),
    duplicateCertificate: (id) => axios.post(`${API_URL}/certificates/duplicate/${id}`),

    getCoursesToCertificate: (id,page, limit, search, sortOrder, authorId) => {
        return axios.get(`${API_URL}/certificates/certificate_courses/${id}`, {
            params: { page, limit, search, sort: sortOrder, author: authorId }
        });
    },

    //  getCatalogsToCertificate: (id) => {
    //   return axios.get(`${API_URL}/certificates/${id}/catalog_courses`);
    // },
    getCatalogsToCertificate: (id, page = 1, limit = 10, search = "") => {
    return axios.get(`${API_URL}/certificates/${id}/catalog_courses`, {
        params: { page, limit, search }
    });
    },

    addCourseToCertificate: (id, courseId) => {
        return axios.post(`${API_URL}/certificates/add_course/${id}`, {
            course_id: courseId
        });
    },
    removeCourseFromCertificate: (id, courseId) => axios.post(`${API_URL}/certificates/remove_course/${id}/${courseId}`),
    getCertificatePreview: (id, courseId) =>
        axios.get(`${API_URL}/certificates/preview/${id}`, {
            params: { course_id: courseId },
            headers: { 'Accept': 'application/json' }
        }),
     
    markCertificateStarted : (id) => axios.post(`${API_URL}/certificates/mark-started/${id}`),

    startCertificate: (id, courseId) =>  axios.post(`${API_URL}/certificates/${id}/start`,{
        course_id: courseId
    }),
    completeCertificate: (clrId, certificateId) => axios.post(`${API_URL}/certificates/certificate-course-completion`, {
        clr_id: clrId,
        certificate_id: certificateId
    }),
}

// Insights services
export const insightsService = {
    getCohorts: () => axios.get(`${API_URL}/insights/cohorts`),
    getUnmetDemand: (params) => axios.get(`${API_URL}/insights/unmet-demand`, { params }),
    getUnmetDemandAction: (params) => axios.get(`${API_URL}/insights/unmet-demand-action`, { params }),
    getFailedSearches: (params) => axios.get(`${API_URL}/insights/failed-searches`, { params }),
    // Removed 12 Oct 2026 (N5 pt 11): getRepeatFailures; the Repeat failures section and its route are gone.
    // Successful searches
    getSuccessfulSearches: (params) => axios.get(`${API_URL}/insights/failed-searches/successful`, { params }),
    // Removed 12 Oct 2026 (N5 pt 7): exportSearchIntelligence; the page is view-only in v1 (DI2, DI3 bring downloads back).
    // Friction Points
    getFrictionPoints: (params) => axios.get(`${API_URL}/insights/friction-points`, { params }),
    getFrictionPointsAction: (params) => axios.get(`${API_URL}/insights/friction-points-action`, { params }),
    // Removed 14 Oct 2026 (N5 pt 18): getLowEngagementContent and archiveContent; Content Health's Low engagement
    // section and its routes are switched off.
    // Quick Stats
    getInternalStats: () => axios.get(`${API_URL}/insights/quick-stats/internal`),
    getExternalStats: () => axios.get(`${API_URL}/insights/quick-stats/external`),
    // The selected period goes with it (K6.1 pt 7). Removed 11 Oct 2026 (K6.1 pt 7): no parameters, so always 7 days.
    getPopularSearches: (params) => axios.get(`${API_URL}/insights/quick-stats/popular-searches`, { params }),
    getNeedsAttention: (params) => axios.get(`${API_URL}/insights/needs-attention`, { params }),
    // Removed 12 Oct 2026 (N5 pt 22): getLearnerHealth and getSystemicBlockers; Learner Health and its routes are
    // switched off.
};

// Setup axios interceptors for handling auth tokens and errors
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // attaches slug to every axios call globally
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const slug = pathParts.length >= 2 ? pathParts[1] : null;
        if (slug) {
            config.headers['X-Client-Slug'] = slug;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Set once a 401 below starts loading the sign-in page, so the app's own guards (Layout, PrivateRoute) wait for that load
// instead of first showing the sign-in page in the page being left: that early copy would use up the once-only notice an
// ended session leaves ('Your account is inactive.', S-6.1 pt 5.2, PR #8), and the real load would then open without it.
let leavingForSignIn = false;
export const isLeavingForSignIn = () => leavingForSignIn;

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        // Check if we're in embedded mode
        const isEmbedded = window.location.pathname.startsWith('/embed');
        if (error.response?.status === 401 && !isEmbedded && window.location.pathname !== '/login' &&
            !window.location.pathname.includes('/forgot-password') &&
            !window.location.pathname.includes('/reset-password') &&
            !window.location.pathname.includes('/signup')) {
            localStorage.clear();
            leavingForSignIn = true;
            window.location.replace('/login');
        }

        if (error.response?.data?.requiresSkills) {
            // This could trigger a global skills popup if needed
            console.log('Skills required for this action');
        }
        return Promise.reject(error);
    }
);

const apiServices = {
    authService,
    dashboardService,
    contentService,
    userService,
    groupService,
    courseService,
    activeFieldService,
    learningPathService,
    settingsService,
    notificationService,
    feedbackService,
    skillService,
    jobRoleService,
    jobAidService,
    reportService,
    insightsService
};

export default apiServices;